import { Camera, ArrowRight } from 'lucide-react';
import { Select, FormInput } from '@/components';
import { City, District, PersonalInfoFormData, PersonalInfoProps } from './personal-info.types';
import clsx from 'clsx';
import '@/assets/styles/components/_personal-info.scss';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { useApi } from '@/api/useApi';
import { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { DocumentTypes, useDocumentUpload } from '@/hooks/use-document-upload';
import { CreatePersonalInformationRequest } from '@dopingtech/live_expert_applicant_bff_api_ts/dist/api';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { validateEmail } from '@/utility';
import { useQueryClient } from 'react-query';

const PersonalInfo: React.FC<PersonalInfoProps> = ({ formData, onFormDataChange, onNext }) => {
	const queryClient = useQueryClient();

	const [isFormActive, setIsFormActive] = useState<boolean>(true);
	const [emailError, setEmailError] = useState<string>('');
	const { getInfoAndUploadToS3 } = useDocumentUpload();
	const { ExpertApplicationControllerApi, LookUpControllerApiApplicant } = useApi();
	const today = dayjs();

	const createPersonalInformation = useMutationProvider((body: CreatePersonalInformationRequest) => ExpertApplicationControllerApi.createPersonalInformation(body), {
		onSuccess: () => {
			queryClient.refetchQueries({
				queryKey: [QUERY_KEYS.getLoggedInExpertApplicantDetailInfo]
			});
			onNext();
		},
		onError: () => setIsFormActive(true)
	});

	const { data: citiesResponse } = useQueryProvider([QUERY_KEYS.getAllCities], () => LookUpControllerApiApplicant.getAllCities(), {
		select: response => response?.data ?? [],
		refetchOnWindowFocus: false
	});

	const { data: districtsResponse } = useQueryProvider(
		[QUERY_KEYS.getAllDistricts, formData.province],
		() => (formData.province ? LookUpControllerApiApplicant.getAllDistricts(formData.province) : null),
		{
			select: response => response?.data ?? [],
			refetchOnWindowFocus: false,
			enabled: !!formData.province
		}
	);

	const cityOptions = useMemo(() => {
		if (!Array.isArray(citiesResponse?.data)) return [];

		return citiesResponse?.data.map((city: City) => ({
			value: city.id.toString(),
			label: city.value
		}));
	}, [citiesResponse]);

	const districtOptions = useMemo(() => {
		if (!Array.isArray(districtsResponse?.data)) return [];

		return districtsResponse?.data.map((district: District) => ({
			value: district.id.toString(),
			label: district.value
		}));
	}, [districtsResponse]);

	const yearOptions = useMemo(() => {
		const currentYear = today.year();
		return Array.from({ length: 100 }, (_, i) => {
			const year = currentYear - i;
			return { value: String(year), label: String(year) };
		});
	}, []);

	const monthOptions = useMemo(() => {
		const selectedYear = parseInt(formData.birthYear || '0');
		const currentYear = today.year();
		const currentMonth = today.month() + 1;

		const maxMonth = selectedYear === currentYear ? currentMonth : 12;

		return Array.from({ length: maxMonth }, (_, i) => ({
			value: String(i + 1).padStart(2, '0'),
			label: String(i + 1).padStart(2, '0')
		}));
	}, [formData.birthYear]);

	const dayOptions = useMemo(() => {
		if (!formData.birthMonth || !formData.birthYear) return [];

		const selectedDate = dayjs(`${formData.birthYear}-${formData.birthMonth}-01`);
		const daysInMonth = selectedDate.daysInMonth();
		const selectedYear = parseInt(formData.birthYear);
		const selectedMonth = parseInt(formData.birthMonth);
		const currentYear = today.year();
		const currentMonth = today.month() + 1;
		const currentDay = today.date();

		const maxDay = selectedYear === currentYear && selectedMonth === currentMonth ? currentDay : daysInMonth;

		return Array.from({ length: maxDay }, (_, i) => ({
			value: String(i + 1).padStart(2, '0'),
			label: String(i + 1).padStart(2, '0')
		}));
	}, [formData.birthMonth, formData.birthYear]);

	const handleDateChange = (field: string, value: string) => {
		const updates: Partial<PersonalInfoFormData> = { [field]: value };

		const year = field === 'birthYear' ? value : formData.birthYear;
		const month = field === 'birthMonth' ? value : formData.birthMonth;
		const day = field === 'birthDay' ? value : formData.birthDay;

		if (year && month && day) {
			const selectedDate = dayjs(`${year}-${month}-${day}`);

			if (selectedDate.isAfter(today)) {
				updates.birthYear = String(today.year());
				updates.birthMonth = String(today.month() + 1).padStart(2, '0');
				updates.birthDay = String(today.date()).padStart(2, '0');
			} else if (parseInt(day) > selectedDate.daysInMonth()) {
				updates.birthDay = String(selectedDate.daysInMonth()).padStart(2, '0');
			}
		}

		if (field === 'birthMonth' && formData.birthDay) {
			const newDate = dayjs(`${formData.birthYear}-${value}-01`);
			const maxDay = newDate.daysInMonth();
			if (parseInt(formData.birthDay) > maxDay) {
				updates.birthDay = String(maxDay).padStart(2, '0');
			}
		}

		if (field === 'birthYear' || field === 'birthMonth') {
			updates.birthDay = '';
		}

		onFormDataChange(updates);
	};

	const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			const reader = new FileReader();
			reader.onloadend = () => {
				onFormDataChange({
					photo: file,
					photoPreview: reader.result as string
				});
			};
			reader.readAsDataURL(file);
		}
	};

	const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		onFormDataChange({
			province: e.target.value,
			district: ''
		});
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsFormActive(false);

		if (formData.email && !validateEmail(formData.email)) {
			setEmailError('Geçersiz e-posta biçimi! Lütfen doğru formatta bir e-posta adresi girin.');
			setIsFormActive(true);
			return;
		}

		try {
			const uploadResult = await getInfoAndUploadToS3(formData.photo, DocumentTypes.IMAGE);

			const selectedCity = cityOptions.find(city => city.value === formData.province);
			const selectedDistrict = districtOptions.find(district => district.value === formData.district);

			await createPersonalInformation.mutateAsync({
				birthday: `${formData.birthYear}-${formData.birthMonth}-${formData.birthDay}`,
				cityCode: formData.province,
				cityName: selectedCity?.label || '',
				districtCode: formData.district,
				districtName: selectedDistrict?.label || '',
				email: formData.email,
				profilePicture: uploadResult?.filePath
			});
		} finally {
			setIsFormActive(true);
		}
	};

	const isFormValid = () => {
		const requiredFields = {
			photo: formData.photo,
			photoPreview: formData.photoPreview,
			birthDay: formData.birthDay,
			birthMonth: formData.birthMonth,
			birthYear: formData.birthYear,
			province: formData.province,
			district: formData.district,
			email: formData.email
		};

		return Object.values(requiredFields).every(value => Boolean(value)) && isFormActive;
	};

	return (
		<div className="personal-info-container">
			<form
				className="personal-info-form"
				onSubmit={handleSubmit}>
				<div className="form-header">
					<h1>Kişisel Bilgiler</h1>
					<p>İletişim bilgilerinizi lütfen eksiksiz ve doğru şekilde doldurun.</p>
				</div>

				<div className="form-section">
					<h3>Fotoğraf</h3>
					<div className="photo-section">
						<div className="avatar-container">
							<img
								src={formData.photoPreview || '/assets/guide-expert.svg'}
								alt="Avatar"
								className="avatar-image"
							/>
						</div>
						<div className="photo-info">
							<div className="info-text">
								<i className="icons-info-circle info-icon" />
								<p>Yüzünüzün net göründüğü bir fotoğraf yükleyerek, başvurunuzu ön plana çıkarabilirsiniz.</p>
							</div>
							<button
								type="button"
								className="upload-button"
								onClick={() => document.getElementById('photo-input')?.click()}>
								<Camera size={20} />
								<span>Fotoğraf Yükle</span>
							</button>
							<input
								id="photo-input"
								type="file"
								accept="image/*"
								onChange={handlePhotoChange}
								className="hidden"
							/>
						</div>
					</div>
				</div>

				<div className="form-section">
					<h3>Doğum Tarihi</h3>
					<div className="birth-date-inputs">
						<Select
							options={yearOptions}
							placeholder="Yıl"
							value={formData.birthYear}
							onChange={e => handleDateChange('birthYear', e.target.value)}
						/>
						<Select
							options={monthOptions}
							placeholder="Ay"
							value={formData.birthMonth}
							onChange={e => handleDateChange('birthMonth', e.target.value)}
							disabled={!formData.birthYear}
						/>
						<Select
							options={dayOptions}
							placeholder="Gün"
							value={formData.birthDay}
							onChange={e => handleDateChange('birthDay', e.target.value)}
							disabled={!formData.birthMonth}
						/>
					</div>
				</div>

				<div className="form-section">
					<h3>İl / İlçe</h3>
					<div className="location-inputs">
						<Select
							options={cityOptions}
							value={formData.province}
							onChange={handleCityChange}
							placeholder="İl seçin"
						/>
						<Select
							options={districtOptions}
							value={formData.district}
							onChange={e => onFormDataChange({ district: e.target.value })}
							placeholder="İlçe seçin"
							disabled={!formData.province}
						/>
					</div>
				</div>

				<div className="form-section">
					<h3>E-Posta</h3>
					<FormInput
						type="text"
						placeholder="ör: ahmet@dopinghafizabirebir.com"
						value={formData.email}
						onChange={e => onFormDataChange({ email: e.target.value })}
					/>
					{emailError && (
						<div className="error-message">
							<i className="icons-info-circle" /> {emailError}
						</div>
					)}
				</div>

				<button
					type="submit"
					className={clsx('submit-button', {
						active: isFormValid()
					})}
					disabled={!isFormValid()}>
					<span>Devam Et</span>
					<ArrowRight size={20} />
				</button>
			</form>
		</div>
	);
};

export default PersonalInfo;
